import React, { useMemo, useLayoutEffect, useRef, useState } from "react";
import PopoverMechanism, { closeActivePopover } from "./PopoverMechanism"
import classNames from "classnames";
import styles from "./MenuPopover.module.scss";
import { ReactComponent as MenuCircleIcon } from "./menu_circle.svg";
import { OvalCircleIcon as OvalIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import _ from "lodash";
import { CopyButton } from "../Widgets";
import tooltips from "../Widgets/Tooltip.module.scss";
import buttons from "./../buttons.module.scss";
import Tooltip from "../Widgets/Tooltip";
import { DropdownArrowDownIcon } from "@netapp/bxp-style/react-icons/Navigation";

/**
 * @deprecated - DONT USE IN NEW FEATURES. Use "ActionMenu" instead.
 */

export const MenuItem = ({ children, onClick, disabled, tooltip }) => {
    return <button type="button" className={styles["menu-item"]} disabled={disabled} onClick={(e) => {
        const keepMenuOpen = onClick(e);
        if (!keepMenuOpen) {
            closeActivePopover();
        }
    }}>
        {_.isArray(children) ? children.map(child => {
            if (_.isString(child)) {
                return <span>{child}</span>
            }

            return child;
        }) : children}
        {tooltip && <div className={classNames(styles.tooltip, tooltips.content)}>{tooltip}</div>}
    </button>
};

export const CopyMenuItem = ({ value, label, hideValue }) => {
    return <span className={styles["menu-item"]}>
        <span>{label}{!hideValue && `: ${value}`}</span>
        <CopyButton text={value} label={label} />
    </span>
};

const Popover = ({ contentClassName, contentStyle, menu: _menu, menuChildren }) => {
    const [verticalPlacement, setVerticalPlacement] = useState(false);
    const menuRef = useRef();
    const menu = useMemo(() => {
        if (menuChildren) {
            if (_.isArray(menuChildren)) {
                return menuChildren
            } else {
                return [menuChildren]
            }
        } else {
            return _.filter(_.isFunction(_menu) ? _menu() : _menu, _.identity);
        }
    }, [_menu, menuChildren]);

    useLayoutEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            if (rect.height + rect.top > window.innerHeight) {
                setVerticalPlacement(true);
            }
        }
    }, [menu])

    return <>
        <div className={classNames(styles["content"], { [styles['popover-menu-vertical']]: verticalPlacement }, contentClassName)} style={contentStyle} ref={menuRef}>
            {menuChildren && menu.map((child, index) => {
                if (!child) {
                    return ""
                }
                const needClone = child.type !== MenuItem && child.type !== CopyMenuItem && !child?.props?.className;
                if (needClone && child?.props.disabled) {
                    return <MenuItem key={index} tooltip={child.props.tooltip} disabled={true}>
                        {child?.props.children}
                    </MenuItem>
                }

                const clone = needClone ? React.cloneElement(child, {
                    className: styles["menu-item"],
                    onClick: (e) => {
                        const keepMenuOpen = child.onClick ? child.onClick(e) : false;
                        if (!keepMenuOpen) {
                            closeActivePopover();
                        }
                    }
                },
                    _.isArray(child?.props?.children) ? child.props.children.map(child => {
                        if (_.isString(child)) {
                            return <span>{child}</span>
                        }

                        return child;
                    }) : child?.props?.children) : child;
                return <React.Fragment key={index}>
                    {clone}
                </React.Fragment>
            })}
            {!menuChildren && menu.map(({ key, label, onClick, Component, Icon, iconStyle, disabled, tooltip }, index) => {
                if (Component) {
                    return <Component key={key || label || index} className={styles["menu-item"]} />
                } else if (label) {
                    return <MenuItem key={key || label || index} onClick={onClick} disabled={disabled} tooltip={tooltip}>
                        {Icon && <Icon style={iconStyle} />}
                        <span>{label}</span>
                    </MenuItem>
                }
            })}
        </div>
    </>;
};

export const ActionMenuContent = React.memo(({ children, className, style }) => {
    children = (!children || _.isArray(children)) ? children : [children];

    return <div className={classNames(styles.content, className)} style={style}>
        {children && children.map((child, index) => {
            if (!child) {
                return ""
            }

            const needClone = child.type !== MenuItem && child.type !== CopyMenuItem;
            if (needClone && child?.props.disabled) {
                return <MenuItem key={index} tooltip={child.props.tooltip} disabled={true}>
                    {child?.props.children}
                </MenuItem>
            }

            const clone = needClone ? React.cloneElement(child, {
                className: styles["menu-item"],
                onClick: (e) => {
                    const keepMenuOpen = child.onClick ? child.onClick(e) : false;
                    if (!keepMenuOpen) {
                        closeActivePopover();
                    }
                }
            }) : child;
            return <React.Fragment key={index}>
                {clone}
            </React.Fragment>
        })}
    </div>
});

const OvalTrigger = ({ isActive }) => {
    return <span aria-label="Options" className={classNames(styles['oval-trigger'], { [styles['oval-trigger-active']]: isActive })}>
        <OvalIcon />
    </span>

};

export const TextWithArrowTrigger = ({ isActive, children, disabled, className, style }) => {
    return <div className={classNames(styles['text-with-arrow-trigger'], className, { [styles['disabled']]: disabled, [styles['active']]: isActive })} style={style}>
        <span>{children}</span>
        <DropdownArrowDownIcon />
    </div>
}

export const MenuTriggerIcon = ({ isActive, Icon, className, ariaLabel = "Menu" }) => {
    return <Icon aria-label={ariaLabel} className={classNames(className, [styles['trigger']], { [styles['trigger-active']]: isActive })} />
};

const BigTrigger = ({ isActive }) => {
    return <MenuTriggerIcon Icon={MenuCircleIcon} isActive={isActive} className={styles['large-trigger']} />
};

const SmallTrigger = ({ isActive }) => {
    return <MenuTriggerIcon Icon={MenuCircleIcon} isActive={isActive} className={styles['small-trigger']} />
};

export const DropdownButton = ({ buttonRef, isActive, children, color = "primary", large, isDisabled, disabled, tooltip, onClick, className }) => {
    return <button onClick={onClick} ref={buttonRef} type="button" disabled={disabled || isDisabled} className={classNames(buttons[color], buttons['dropdown-button'], { [buttons.large]: large }, className)}>
        {children}
        <span className={classNames(buttons['dropdown-button-triangle'], { [buttons["active-triangle"]]: isActive })} />
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </button>
};

export const RoundDropdownButton = ({ buttonRef, isActive, children, color = "primary", large, isDisabled, disabled, tooltip, onClick, style, className }) => {
    return <button onClick={onClick} ref={buttonRef} type="button" disabled={disabled || isDisabled} className={classNames(buttons[color], buttons['round-dropdown-button'], { [buttons.large]: large }, className)} style={style}>
        {children}
        <span className={classNames(buttons['dropdown-button-triangle'], { [buttons["active-triangle"]]: isActive })} />
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </button>
};

/**
 *
 triggerClassName,
 popoverClassName, popoverStyle, children,
 arrowClassName,
 arrowStyle,
 contentClassName,
 contentStyle,
 menu
 */
const MenuPopover = ({ popoverClassName, Trigger: _Trigger, TriggerButton, children, disabled, tooltip, ...props }) => {
    let Trigger = BigTrigger;

    if (_Trigger === "oval") {
        Trigger = OvalTrigger
    } else if (_Trigger === "small") {
        Trigger = SmallTrigger
    } else if (_Trigger) {
        Trigger = _Trigger;
    }

    return <PopoverMechanism popoverClassName={classNames(styles['menu-popover'], popoverClassName)} disabled={disabled} tooltip={tooltip} {...props} menuChildren={children} Popover={Popover} Trigger={Trigger} TriggerButton={TriggerButton}
        triggerOnClick={true} />
};

export const ButtonStyleMenuPopover = ({ round, buttonChildren, large, color, popoverClassName, triggerClassName, ...props }) => {
    const TriggerButton = round ? RoundDropdownButton : DropdownButton;
    return <MenuPopover {...props} popoverClassName={classNames(styles['button-style-menu-popover'], popoverClassName)} renderPosition="right"
        TriggerButton={props => <TriggerButton {...props} large={large} color={color} className={triggerClassName}>{buttonChildren}</TriggerButton>} />
}

export default MenuPopover;